import { getToken, refreshToken } from '@polleverywhere/fetch/lib/csrf'
import { authExchange } from '@urql/exchange-auth'

interface CsrfAuthState {
  csrfToken: string
}

function isCsrfAuthState (authState: unknown): authState is CsrfAuthState {
  return typeof authState === 'object' && !!authState && 'csrfToken' in authState
}

export function setupCsrfTokenExchange () {
  return authExchange({
    addAuthToOperation ({ authState, operation }) {
      // The token isn't in the auth state, return the operation without changes
      if (!isCsrfAuthState(authState)) {
        return operation
      }

      // fetchOptions may be a function (See Client API)
      const fetchOptions =
        typeof operation.context.fetchOptions === 'function'
          ? operation.context.fetchOptions()
          : operation.context.fetchOptions || {}

      return {
        ...operation,
        context: {
          ...operation.context,
          fetchOptions: {
            ...fetchOptions,
            headers: {
              ...fetchOptions.headers,
              'X-CSRF-Token': authState.csrfToken
            }
          }
        }
      }
    },
    willAuthError: ({ authState }) => {
      // If there's no authState, we'll surely need to get a CSRF token first
      if (!authState) return true
      // Otherwise we should be OK, additional heuristics could be added here if needed
      return false
    },
    didAuthError: ({ error }) => {
      // Check if the error was a CSRF error
      return error.graphQLErrors.some(
        e => e.extensions?.type === 'InvalidAuthenticityToken'
      )
    },
    getAuth: async ({ authState }) => {
      // For initial launch, just grab the current token
      if (!authState) {
        const csrfToken = await getToken()
        return { csrfToken }
      }

      // This came from an auth error, refresh
      const csrfToken = await refreshToken()
      return { csrfToken }
    }
  })
}
